import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle2,
  ImageOff,
  Package,
  Pencil,
  Tag,
} from "lucide-react";

import {
  Button,
  EmptyState,
  ErrorState,
  PageHeader,
  SearchInput,
  SkeletonRows,
  Surface,
  cn,
} from "../../../components/ui";
import OpenWorkQueueCards from "../../components/OpenWorkQueueCards";
import { businessProductsAPI } from "../../services/businessProductsService";

function classifyStock(stock, threshold = 10) {
  const quantity = Number(stock ?? 0);
  if (quantity <= 0) return "out";
  if (quantity <= threshold) return "low";
  return "in";
}

const QUEUES = [
  {
    id: "draft",
    summaryKey: "draft",
    title: "Draft products",
    description: "Products that are not ready to publish.",
    actionLabel: "Review drafts",
    rowAction: "Review product",
    emptyTitle: "No draft products",
    emptyDescription: "There are no products left in draft status.",
    searchLabel: "Search draft products",
    icon: Pencil,
    tone: "warning",
    issue: "Product is still in draft",
    meta: (product) => `${product.category_name || "Uncategorized"} · Status ${product.status || "DRAFT"}`,
  },
  {
    id: "category",
    summaryKey: "missingCategory",
    title: "Missing category",
    description: "Products that cannot be organized properly.",
    actionLabel: "Assign categories",
    rowAction: "Assign category",
    emptyTitle: "No category gaps",
    emptyDescription: "All products currently have a category assignment.",
    searchLabel: "Search products missing a category",
    icon: Tag,
    tone: "danger",
    issue: "Category assignment is missing",
    meta: (product) => `Status ${product.status || "Unknown"}`,
  },
  {
    id: "media",
    summaryKey: "missingMedia",
    title: "Missing media",
    description: "Products without a primary catalog image.",
    actionLabel: "Add product media",
    rowAction: "Add media",
    emptyTitle: "No missing media",
    emptyDescription: "All products currently have a catalog image.",
    searchLabel: "Search products missing media",
    icon: ImageOff,
    tone: "accent",
    issue: "Primary catalog image is missing",
    meta: (product) => `${product.category_name || "Uncategorized"} · ${product.status || "Unknown"}`,
  },
  {
    id: "stock",
    summaryKey: "lowStock",
    title: "Low stock",
    description: "Listed products approaching their stock threshold.",
    actionLabel: "Review stock",
    rowAction: "Review inventory",
    emptyTitle: "No low-stock products",
    emptyDescription: "No listed product is currently under low-stock pressure.",
    searchLabel: "Search low-stock products",
    icon: AlertTriangle,
    tone: "warning",
    issue: "Stock is at or below its threshold",
    meta: (product) => `Stock ${Number(product.stock || 0)} · Threshold ${Number(product.low_stock_threshold ?? 10)}`,
  },
];

function ProductRow({ product, queue }) {
  const IssueIcon = queue.icon;

  return (
    <li className="flex min-h-touch flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0 sm:flex sm:flex-1 sm:items-center sm:gap-6">
        <div className="min-w-0 sm:w-2/5">
          <p className="truncate text-label text-foreground">{product.name || `Product #${product.id}`}</p>
          <p className="mt-1 truncate text-meta text-tertiary">{queue.meta(product)}</p>
        </div>
        <p className={cn(
          "mt-2 flex items-center gap-2 text-body-sm sm:mt-0 sm:flex-1",
          queue.tone === "danger" ? "text-danger" : queue.tone === "warning" ? "text-warning" : "text-accent",
        )}>
          <IssueIcon aria-hidden="true" className="h-4 w-4 shrink-0" />
          {queue.issue}
        </p>
      </div>
      <Button
        as={Link}
        to={`/business/products/${product.id}`}
        size="sm"
        variant="secondary"
        className="w-full border-accent/30 bg-accent-subtle/40 text-accent hover:border-accent hover:bg-accent-subtle sm:w-auto"
      >
        {queue.rowAction}
      </Button>
    </li>
  );
}

export default function MerchandisingOpenWork() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [query, setQuery] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await businessProductsAPI.getProductSalesReport();
      const products = Array.isArray(res)
        ? res
        : Array.isArray(res?.data?.items)
          ? res.data.items
          : Array.isArray(res?.data)
            ? res.data
            : Array.isArray(res?.items)
              ? res.items
              : [];
      setRows(products);
    } catch {
      setRows([]);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const summary = useMemo(() => ({
    draft: rows.filter((row) => String(row.status || "").toUpperCase() === "DRAFT"),
    missingCategory: rows.filter((row) => row.category_id == null),
    missingMedia: rows.filter((row) => !row.image),
    lowStock: rows.filter((row) => (
      String(row.status || "").toUpperCase() === "LISTED"
      && classifyStock(row.stock, row.low_stock_threshold ?? 10) === "low"
    )),
  }), [rows]);

  const requestedLane = searchParams.get("lane");
  const activeLane = QUEUES.some((queue) => queue.id === requestedLane)
    ? requestedLane
    : QUEUES.find((queue) => summary[queue.summaryKey].length > 0)?.id ?? QUEUES[0].id;
  const activeQueue = QUEUES.find((queue) => queue.id === activeLane) || QUEUES[0];
  const activeRows = summary[activeQueue.summaryKey];
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const filteredRows = normalizedQuery
    ? activeRows.filter((product) => [
      product.name,
      product.sku,
      product.product_sku,
      product.category_name,
      product.status,
      product.id,
    ].some((value) => String(value ?? "").toLocaleLowerCase().includes(normalizedQuery)))
    : activeRows;

  function selectQueue(nextLane) {
    setSearchParams({ lane: nextLane });
    setQuery("");
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Merchandising Open Work"
        description="Resolve catalog issues that affect product readiness and storefront presentation."
        actions={(
          <Button as={Link} to="/business/products" size="sm" icon={Package}>
            Open products
          </Button>
        )}
      />

      <OpenWorkQueueCards
        queues={QUEUES.map((queue) => ({ ...queue, count: summary[queue.summaryKey].length }))}
        active={activeLane}
        loading={loading}
        onChange={selectQueue}
        idPrefix="merchandising-queue"
        panelId="merchandising-work-panel"
        label="Merchandising work queues"
        countNoun="products"
      />

      <Surface
        as="section"
        id="merchandising-work-panel"
        role="tabpanel"
        aria-labelledby={`merchandising-queue-${activeQueue.id}`}
        toolbar={(
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-section-title text-foreground">{activeQueue.title}</h2>
              <p className="mt-1 text-body-sm text-secondary">
                {loading ? "Loading products…" : `${activeRows.length} ${activeRows.length === 1 ? "product needs" : "products need"} attention`}
              </p>
            </div>
            <SearchInput
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              label={activeQueue.searchLabel}
              placeholder="Search by product, SKU, or category"
              className="w-full sm:w-80"
            />
          </div>
        )}
      >
        {loading ? <SkeletonRows rows={5} columns={3} label="Loading merchandising work" /> : null}

        {!loading && loadError ? (
          <ErrorState
            title="Merchandising work could not be loaded"
            description="Your catalog has not been changed. Check your connection and try again."
            onRetry={load}
          />
        ) : null}

        {!loading && !loadError && !activeRows.length ? (
          <EmptyState
            icon={CheckCircle2}
            title={activeQueue.emptyTitle}
            description={activeQueue.emptyDescription}
          />
        ) : null}

        {!loading && !loadError && activeRows.length > 0 && !filteredRows.length ? (
          <EmptyState
            title="No matching products"
            description={`No ${activeQueue.title.toLocaleLowerCase()} match “${query.trim()}”.`}
            action={<Button size="sm" onClick={() => setQuery("")}>Clear search</Button>}
          />
        ) : null}

        {!loading && !loadError && filteredRows.length > 0 ? (
          <ul className="divide-y divide-subtle">
            {filteredRows.slice(0, 12).map((product) => (
              <ProductRow key={`${activeQueue.id}-${product.id}`} product={product} queue={activeQueue} />
            ))}
          </ul>
        ) : null}
      </Surface>
    </div>
  );
}
